import { useLocation } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Box, FactBox, ListBox } from "@/components/ui/box.schema";
import { usePersonVisit } from "@/components/people/visit/person-visit";
import { imam, queryRead, readOf, useFieldSaves } from "@/components/people/visit/commit";
import { savedField } from "@/components/people/visit/fields";
import { currentOnConflict } from "@/lib/api/conflict";
import { useAuth } from "@/contexts/AuthContext";
import { HONORIFICS } from "@/lib/honorifics";
import { facultyApi, type FacultyProfile, type FacultyCourseLoad } from "@/lib/api/faculty";
import { personTitle } from "@/lib/person-name";
import type { VisitLevel } from "@/lib/visit/address";
import { shellOf, type VisitResolution } from "@/lib/visit/registry";
import { facultyCopy as c } from "./faculty-copy";
import { ApiError } from "@/lib/api/client";

/** Faculty viewers read only the tenant-visible directory projection. Registrar jobs keep A5.1's person read. */
export function useFacultyPersonVisit(level: VisitLevel | null): VisitResolution | null {
  const { me } = useAuth();
  const client = useQueryClient();
  const { pathname } = useLocation();
  const shell = shellOf(pathname);
  const member = shell === "instructor" || shell === "student";
  const registrar = usePersonVisit(member ? null : level);
  const query = useQuery({ queryKey: ["faculty"], queryFn: () => facultyApi.list(), enabled: member && Boolean(level), retry: false });
  const forbidden = query.isError && query.error instanceof ApiError && [401, 403, 404].includes(query.error.status);
  const profile = !forbidden ? query.data?.find(row => row.person_id === level?.id) : undefined;
  const self = shell === "instructor" && Boolean(me?.user_id && profile?.user_id === me.user_id);
  const saves = useFieldSaves<FacultyProfile, "honorific" | "email">({
    record: self ? profile ?? null : null,
    identity: row => row.id,
    version: row => row.person_updated_at ?? row.updated_at,
    fields: ["honorific", "email"],
    read: (row, field) => row[field] ?? "",
    send: async (field, value, base, key) => ({ ...base, ...await facultyApi.patch(base.id, {
      [field]: value.trim() || null, expected_updated_at: base.updated_at, expected_person_updated_at: base.person_updated_at,
    }, key) }),
    conflictOf: error => {
      const current = currentOnConflict<FacultyProfile>(error);
      return current && profile && current.id === profile.id ? { ...profile, ...current } : null;
    },
    reread: async () => {
      const row = (await facultyApi.list()).find(row => row.person_id === level?.id && row.user_id === me?.user_id);
      if (!row) throw new ApiError(404, "This profile is not available.");
      return row;
    },
    onSaved: saved => client.setQueryData<FacultyProfile[]>(["faculty"], rows => rows?.map(row => row.id === saved.id ? saved : row)),
  });
  if (!member) return registrar;
  if (!level) return null;
  if (forbidden) return { status: "refused" };
  if (level.job) return { status: "refused" };
  if (query.data && !profile) return { status: "refused" };
  const reading = queryRead(query, c.failed);
  const one = readOf(reading, rows => rows.find(row => row.person_id === level.id)!);
  const fact: FactBox<FacultyProfile> = { id: "faculty-profile", kind: "fact", label: c.profile, job: "Read the recorded teaching profile.", owner: imam, read: one, fold: "never", detail: () => "",
    ...(self ? { fields: () => [
      savedField(saves, { id: "faculty-self-honorific", field: "honorific", label: "Honorific", theirs: row => row.honorific ?? "", options: { empty: "None", choices: HONORIFICS.map(value => ({ value, label: value })) } }),
      savedField(saves, { id: "faculty-self-email", field: "email", label: "Email", theirs: row => row.email ?? "", type: "email" }),
    ] } : {}),
    body: row => <dl>{Object.entries(c.facts).filter(([key]) => (!self || key !== "honorific" && key !== "email") && row[key as keyof typeof c.facts]).map(([key, label]) => <div key={key}><dt>{label}</dt><dd>{row[key as keyof typeof c.facts]}</dd></div>)}</dl> };
  const assignments: ListBox<FacultyProfile, FacultyCourseLoad> = { id: "faculty-assignments", kind: "list", label: c.assignments, job: "Read this instructor's recorded teaching assignments.", owner: imam, read: profile && !profile.courses.length ? { status: "empty", sentence: c.noAssignments } : one, fold: "never", detail: () => "", state: () => ({ class: "quiet", word: "Recorded" }), rows: row => row.courses,
    row: { key: row => row.course_id, title: row => row.course_title, facts: row => [row.semester_name, row.crn].filter(Boolean).join(" · ") } };
  return { status: "ready", subject: profile ? personTitle(profile.display_name, profile.honorific) : c.title, boxes: [fact, assignments] as Box[] };
}
